// Contract/useMilestoneEnrichment.ts
import { useCallback } from 'react';
import { Milestone, MilestoneAction, ProposedDates, ContractTemplateProps } from './Contract.interface';
import { useDateFormatter } from '../../hooks/useDateFormatter';

interface UseMilestoneEnrichmentProps {
  currentUserId: string;
  isContractor: boolean;
  contract: ContractTemplateProps;
  onNavigateToTab?: (tabIndex: number) => void;
  onSetInspectionDates: () => void;
  onPostInspectionAction: () => void;
  onHomeownerReview: () => void;
  onPaymentAction: (milestoneId: string, amount: number) => void;
}

export const useMilestoneEnrichment = ({
  currentUserId,
  isContractor,
  contract,
  onNavigateToTab,
  onSetInspectionDates,
  onPostInspectionAction,
  onHomeownerReview,
  onPaymentAction
}: UseMilestoneEnrichmentProps) => {
  const { formatProposedDate } = useDateFormatter();

  const enrichMilestone = useCallback((
    milestone: Milestone,
    existingProposedDates: ProposedDates | null,
    existingSelectedDate: string | null
  ): Milestone => {
    let action: MilestoneAction | undefined;
    let updatedMilestone: Milestone = { ...milestone };

    // Handle signing milestones
    if (milestone.type === 'signing') {
      const isHomeownerSigning = milestone.id === 'signing-homeowner' && currentUserId === contract.ownerId;
      const isContractorSigning = milestone.id === 'signing-contractor' && currentUserId === contract.contractorId;

      if ((isHomeownerSigning || isContractorSigning) && milestone.status !== 'completed') {
        action = {
          label: 'Sign Contract',
          type: 'primary',
          onClick: () => onNavigateToTab?.(3),
          disabled: false,
          show: true
        };
      }
      updatedMilestone.action = action;
    }

    // Handle payment milestones
    if (milestone.type === 'payment' && milestone.status === 'pending' && !isContractor) {
      action = {
        label: 'Make Payment',
        type: 'success',
        onClick: () => onPaymentAction(milestone.id, milestone.amount || 0),
        disabled: false,
        show: true
      };
      updatedMilestone.action = action;
    }

    // Handle inspection milestones
    if (milestone.type === 'inspection') {
      updatedMilestone = enrichInspectionMilestone(
        milestone,
        existingProposedDates,
        existingSelectedDate,
        formatProposedDate,
        isContractor,
        contract,
        onSetInspectionDates,
        onPostInspectionAction,
        onHomeownerReview
      );
    }

    return updatedMilestone;
  }, [
    currentUserId,
    isContractor,
    contract,
    formatProposedDate,
    onNavigateToTab,
    onSetInspectionDates,
    onPostInspectionAction,
    onHomeownerReview,
    onPaymentAction
  ]);

  return { enrichMilestone };
};

// Helper function for inspection milestone enrichment
const enrichInspectionMilestone = (
  milestone: Milestone,
  existingProposedDates: ProposedDates | null,
  existingSelectedDate: string | null,
  formatProposedDate: (date: any) => string,
  isContractor: boolean,
  contract: ContractTemplateProps,
  onSetInspectionDates: () => void,
  onPostInspectionAction: () => void,
  onHomeownerReview: () => void
): Milestone => {
  let updatedMilestone = { ...milestone };

  switch (milestone.id) {
    case 'inspection-setup':
      if (milestone.proposedDates) {
        updatedMilestone.description = `Proposed dates:\n` +
          `Preferred: ${formatProposedDate(milestone.proposedDates.preferred)}\n` +
          `Secondary: ${formatProposedDate(milestone.proposedDates.secondary)}\n` +
          `Tertiary: ${formatProposedDate(milestone.proposedDates.tertiary)}`;
      }

      if (isContractor && milestone.status !== 'completed') {
        updatedMilestone.action = {
          label: 'Set Inspection Dates',
          type: 'primary',
          onClick: onSetInspectionDates,
          disabled: false,
          show: true
        };
      }
      break;

    case 'inspection-date-selection':
      const hasProposedDates = existingProposedDates !== null;
      const setupComplete = contract.milestones.find(m => m.id === 'inspection-setup')?.status === 'completed';

      if (milestone.selectedDate) {
        updatedMilestone = {
          ...updatedMilestone,
          status: 'completed',
          description: `Inspection scheduled for ${formatProposedDate(milestone.selectedDate)}`
        };
      } else if (!existingSelectedDate && setupComplete && hasProposedDates) {
        updatedMilestone = {
          ...updatedMilestone,
          status: 'active',
          description: 'Please select one of the proposed inspection dates',
          action: !isContractor ? {
            label: 'Select Date',
            type: 'success',
            onClick: onSetInspectionDates,
            disabled: false,
            show: true
          } : undefined
        };
      }
      break;

    case 'post-inspection-contractor':
      if (isContractor) {
        const dateSelectionMilestone = contract.milestones.find(m => m.id === 'inspection-date-selection');
        const isDateSelected = dateSelectionMilestone?.status === 'completed';

        if (isDateSelected) {
          updatedMilestone = {
            ...updatedMilestone,
            status: 'active',
            action: {
              label: 'Post-Inspection Action',
              type: 'warning',
              onClick: onPostInspectionAction,
              disabled: false,
              show: true
            }
          };
        }
      }
      break;

    case 'post-inspection-homeowner':
      if (!isContractor && milestone.status === 'pending') {
        updatedMilestone.action = {
          label: 'Review Changes',
          type: 'info',
          onClick: onHomeownerReview,
          disabled: false,
          show: true
        };
      }
      break;
  }

  return updatedMilestone;
};